/* ============================================================
   VD Clinician Landing Page — shared behaviour
   • Scroll-reveal (IntersectionObserver, respects reduced-motion)
   • Before/after gallery slider (2-up desktop / 1-up mobile)
   • Mobile "Read more" quote toggles
   Vanilla JS, no deps, idempotent, theme-editor aware.
   ============================================================ */
(function () {
  'use strict';

  /* The file may be referenced by multiple sections; run setup only once. */
  if (window.__vdclLandingLoaded) return;
  window.__vdclLandingLoaded = true;

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------- Scroll reveal ---------- */
  function initReveal(scope) {
    var root = scope || document;
    var nodes = root.querySelectorAll('[data-vdcl-reveal]');
    if (!nodes.length) return;

    document.documentElement.classList.add('vdcl-anim');

    if (reduceMotion || !('IntersectionObserver' in window)) {
      nodes.forEach(function (n) { n.classList.add('is-revealed'); });
      return;
    }

    var io = new IntersectionObserver(function (entries, obs) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-revealed');
          obs.unobserve(entry.target);
        }
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -8% 0px' });

    nodes.forEach(function (n) { io.observe(n); });
  }

  /* ---------- Before/after slider ---------- */
  function visibleCount() {
    return window.matchMedia('(max-width: 989px)').matches ? 1 : 2;
  }

  function initBA(scope) {
    var root = scope || document;
    var sliders = root.querySelectorAll('[data-vdcl-ba]');

    sliders.forEach(function (slider) {
      if (slider.dataset.vdclBaInit === '1') return;
      slider.dataset.vdclBaInit = '1';

      var track = slider.querySelector('[data-vdcl-ba-track]');
      var cards = Array.prototype.slice.call(slider.querySelectorAll('.vdcl-ba__card'));
      var prevBtns = slider.querySelectorAll('[data-vdcl-ba-prev]');
      var nextBtns = slider.querySelectorAll('[data-vdcl-ba-next]');
      var dotsWrap = slider.querySelector('[data-vdcl-ba-dots]');
      if (!track || !cards.length) return;

      var index = 0;

      function maxIndex() { return Math.max(0, cards.length - visibleCount()); }

      function buildDots() {
        if (!dotsWrap) return;
        dotsWrap.innerHTML = '';
        var per = visibleCount();
        var pages = Math.ceil(cards.length / per);
        for (var i = 0; i < pages; i++) {
          var dot = document.createElement('button');
          dot.type = 'button';
          dot.className = 'vdcl-ba__dot';
          dot.setAttribute('aria-label', 'Go to transformation ' + (i + 1));
          (function (i) {
            dot.addEventListener('click', function () { go(i * per); });
          })(i);
          dotsWrap.appendChild(dot);
        }
      }

      function update() {
        if (index > maxIndex()) index = maxIndex();
        var card = cards[0];
        var gap = parseFloat(getComputedStyle(track).columnGap || getComputedStyle(track).gap || '0') || 0;
        var step = card.getBoundingClientRect().width + gap;
        track.style.transform = 'translateX(' + (-index * step) + 'px)';

        if (dotsWrap) {
          var activePage = Math.round(index / visibleCount());
          var dots = dotsWrap.children;
          for (var i = 0; i < dots.length; i++) {
            dots[i].classList.toggle('is-active', i === activePage);
            dots[i].setAttribute('aria-current', i === activePage ? 'true' : 'false');
          }
        }
        prevBtns.forEach(function (b) { b.disabled = index <= 0; });
        nextBtns.forEach(function (b) { b.disabled = index >= maxIndex(); });
      }

      function go(i) {
        index = Math.min(Math.max(i, 0), maxIndex());
        update();
      }

      prevBtns.forEach(function (b) { b.addEventListener('click', function () { go(index - visibleCount()); }); });
      nextBtns.forEach(function (b) { b.addEventListener('click', function () { go(index + visibleCount()); }); });

      var resizeTimer;
      window.addEventListener('resize', function () {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(function () { buildDots(); update(); }, 150);
      });

      buildDots();
      update();
    });
  }

  /* ---------- Mobile read-more ---------- */
  function initReadMore(scope) {
    var root = scope || document;
    var btns = root.querySelectorAll('[data-vdcl-readmore]');
    btns.forEach(function (btn) {
      if (btn.dataset.vdclRmInit === '1') return;
      btn.dataset.vdclRmInit = '1';
      btn.addEventListener('click', function () {
        var target = document.getElementById(btn.getAttribute('aria-controls'));
        if (!target) return;
        var expanded = target.classList.toggle('is-expanded');
        btn.setAttribute('aria-expanded', expanded ? 'true' : 'false');
        btn.textContent = expanded ? 'Read less' : 'Read more';
      });
    });
  }

  /* ---------- Why-clinicians card carousel (mobile only) ---------- */
  function initWhyCarousel(scope) {
    var root = scope || document;
    var sliders = root.querySelectorAll('[data-vdcl-why]');

    sliders.forEach(function (slider) {
      if (slider.dataset.vdclWhyInit === '1') return;
      slider.dataset.vdclWhyInit = '1';

      var track = slider.querySelector('[data-vdcl-why-track]');
      var cards = Array.prototype.slice.call(slider.querySelectorAll('.vdcl-why__card'));
      var prevBtns = slider.querySelectorAll('[data-vdcl-why-prev]');
      var nextBtns = slider.querySelectorAll('[data-vdcl-why-next]');
      if (!track || !cards.length) return;

      // Per-card image slides in the bubble + the default (highlighted) card
      var bubble = slider.querySelector('[data-vdcl-why-bubble]');
      var slides = bubble ? Array.prototype.slice.call(bubble.querySelectorAll('.vdcl-why__bubble-slide')) : [];
      var defaultIndex = 0;
      slides.forEach(function (s, i) { if (s.classList.contains('is-active')) defaultIndex = i; });

      function setActiveImage(i) {
        if (!slides.length) return;
        slides.forEach(function (s, si) {
          var active = si === i;
          s.classList.toggle('is-active', active);
          // Play only the visible slide's video; pause (and reset) the hidden ones
          var vid = s.querySelector('video');
          if (vid) {
            if (active) {
              var p = vid.play();
              if (p && p.catch) p.catch(function () {});
            } else {
              vid.pause();
            }
          }
        });
      }

      var index = 0;

      function isMobile() { return window.matchMedia('(max-width: 989px)').matches; }

      // Desktop: hovering a card swaps the bubble image to that card's image
      cards.forEach(function (card, i) {
        card.addEventListener('mouseenter', function () { if (!isMobile()) setActiveImage(i); });
      });
      slider.addEventListener('mouseleave', function () { if (!isMobile()) setActiveImage(defaultIndex); });

      function update() {
        if (!isMobile()) {
          // desktop: vertical list, no transform
          track.style.transform = '';
          index = 0;
          setActiveImage(defaultIndex);
          prevBtns.forEach(function (b) { b.disabled = true; });
          nextBtns.forEach(function (b) { b.disabled = true; });
          return;
        }
        if (index > cards.length - 1) index = cards.length - 1;
        if (index < 0) index = 0;
        var gap = parseFloat(getComputedStyle(track).columnGap) || 0;
        var step = cards[0].getBoundingClientRect().width + gap;
        track.style.transform = 'translateX(' + (-index * step) + 'px)';
        setActiveImage(index);
        prevBtns.forEach(function (b) { b.disabled = index <= 0; });
        nextBtns.forEach(function (b) { b.disabled = index >= cards.length - 1; });
      }

      prevBtns.forEach(function (b) { b.addEventListener('click', function () { index -= 1; update(); }); });
      nextBtns.forEach(function (b) { b.addEventListener('click', function () { index += 1; update(); }); });

      var resizeTimer;
      window.addEventListener('resize', function () {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(update, 150);
      });

      update();
    });
  }

  function initAll(scope) {
    initReveal(scope);
    initBA(scope);
    initReadMore(scope);
    initWhyCarousel(scope);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(); });
  } else {
    initAll();
  }

  /* Shopify theme editor: re-init the loaded section */
  document.addEventListener('shopify:section:load', function (e) { initAll(e.target); });
}());
